package uk.co.nickthecoder.kyd.appearance

import uk.co.nickthecoder.glok.backend.Texture
import uk.co.nickthecoder.glok.backend.backend
import uk.co.nickthecoder.glok.scene.Color
import uk.co.nickthecoder.glok.scene.Image
import uk.co.nickthecoder.glok.util.Matrix
import uk.co.nickthecoder.kyd.*
import uk.co.nickthecoder.kyd.util.Vector2

/**
 * A [Pose] or [Graphic], both have a [pin] which determines which point in the image
 * refers to [Actor.position].
 *
 */
interface PinnedImage {
    val pin: Vector2
    val image: Image
}

/**
 * A [Resource] corresponding to a `.png` or `.jpeg` file.
 *
 * The contents of this file may be a single image, or a collection of smaller images
 * (often arranged in a grid pattern).
 * Collections of smaller images are called an `Atlases`.
 * However, there is no `Atlas` class.
 * Instead, the [Graphic]'s children will consist of [Pose]s,
 * where the pose defines the rectangular region within the [Graphic].
 */
class Graphic(
    parent: ParentResource,
    name: String,
    override val filename: String

) : AbstractParentResource(parent, name), FileResource, AppearanceResource, PinnedImage {

    override val resourceType: ResourceType
        get() = ResourceType.GRAPHIC

    override fun appearanceForActor(actor: Actor): Appearance {
        val result = if (repeating) {
            texture.setRepeating(x = true, y = true)
            RepeatingGraphic(this).apply {
                val oldBounds = actor.appearance.localBounds()
                // For a "new" actor (in the editor), we use the "default" size,
                // but when the actor already HAS bounds, then we use its previous bound.
                if (oldBounds !== NoAppearance.NO_BOUNDS) {
                    corner0 = oldBounds.first
                    corner2 = oldBounds.second
                }
            }
        } else {
            GraphicAppearance(this)
        }
        result.tint = actor.appearance.tintIfAvailable
        return result
    }

    override fun appearance(): Appearance {
        return if (repeating) {
            RepeatingGraphic(this)
        } else {
            GraphicAppearance(this)
        }
    }

    override var pin: Vector2 = Vector2.ZERO

    var repeating = false

    var atlasGrid: AtlasGrid? = null

    var texture: Texture = parent.game().resources.loadTexture("${parent.pathWithTrailingSlash()}${filename}")

    override val image: Image get() = texture

    val width: Int
        get() = texture.width

    val height: Int
        get() = texture.height



    override fun toString() = "Graphic $name ${texture.width} x ${texture.height} pin @ $pin"
}

class AtlasGrid {

    var marginX: Int = 1
    var marginY: Int = 1

    var spacing: Int = 1

    var poseWidth: Int = 0
    var poseHeight: Int = 0

    override fun toString() = "AtlasGrid $poseHeight x $poseHeight margin $marginX x $marginY spacing $spacing"
}

